import { useEffect, useRef, useState } from "react";
import {
    Alert, Box, Button, Checkbox,
    FormControlLabel, Link, Stack, TextField, Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router";
import { useAuth } from "src/context/AuthContext";
import { useLang, useT, useTRich } from "src/i18n";
import { resolveErrorMessage } from "src/i18n/errorCopy";
import BusyButton from "src/components/BusyButton";
import StepDotList from "src/components/quote/StepDotList";
import { KAYTTOEHDOT_ROUTE, TIETOSUOJA_ROUTE } from "src/utils/quote/quoteTerms";
import { reaffirmAcceptances } from "src/data/legalDocument";

// EmailStep is rendered by AuthModal; RegisterFlow starts at "code".
const REGISTER_STEP_INDEX = { email: 0, code: 1, profile: 2 };

function RegisterStepper({ step }) {
    const t = useT();
    const steps = [t("auth.shared.emailLabel"), t("auth.register.stepCode"), t("auth.register.stepProfile")];
    const idx = REGISTER_STEP_INDEX[step];
    return (
        <Stack spacing={0.5} sx={{ alignItems: "center", mb: 1 }}>
            <StepDotList
                labels={steps}
                currentStep={idx}
                maxReachedStep={-1}
                onJumpTo={() => {}}
                orientation="horizontal"
            />
            <Typography
                variant="caption"
                sx={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 11,
                    textTransform: "uppercase",
                    letterSpacing: "0.10em",
                    color: "text.secondary",
                }}
            >
                {t("auth.register.stepIndicator", { current: idx + 1, total: steps.length, label: steps[idx] })}
            </Typography>
        </Stack>
    );
}

export default function RegisterFlow({ email, onBack }) {
    const { startRegistration, verifyRegistrationCode, completeRegistration } = useAuth();
    const { lang } = useLang();
    const t = useT();
    const tRich = useTRich();

    // step: "code" | "profile". The "email" step is handled by EmailStep before
    // this component mounts; we kick off startRegistration on mount.
    const [step, setStep] = useState("code");
    const [sessionId, setSessionId] = useState(null);
    const [code, setCode] = useState("");
    const [sendError, setSendError] = useState(null);
    const [sendingCode, setSendingCode] = useState(true);
    // Latches the mount-time send so a re-render before startRegistration resolves
    // (e.g. a lang toggle, now that this effect depends on lang/t) can't re-fire it —
    // sessionId alone doesn't guard that window since it's still null until the first
    // call resolves. Mirrors LinkedInCallback.jsx's ranRef pattern.
    const startedRef = useRef(false);

    // Send the verification email on mount.
    useEffect(() => {
        if (startedRef.current) return;
        startedRef.current = true;
        let cancelled = false;
        startRegistration(email.trim())
            .then((sid) => { if (!cancelled) setSessionId(sid); })
            .catch((err) => {
                if (!cancelled) {
                    setSendError(resolveErrorMessage(err, { t, lang, fallbackKey: "auth.registerCodeSendFailed" }));
                }
            })
            .finally(() => { if (!cancelled) setSendingCode(false); });
        return () => { cancelled = true; };
    }, [email, startRegistration, sessionId, lang, t]);

    const [profile, setProfile] = useState({
        etunimi: "", sukunimi: "", puhelin: "", password: "",
        isYritys: false, yritysNimi: "", ytunnus: "",
        legalAccepted: false,
    });
    const [errors, setErrors] = useState({});
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    const updateProfile = (k) => (e) => {
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setProfile((p) => ({ ...p, [k]: value }));
        if (errors[k]) setErrors((p) => ({ ...p, [k]: undefined }));
    };

    const submitCode = async (e) => {
        e.preventDefault();
        if (!code.trim()) {
            setError(t("auth.register.verifyCodeRequired"));
            return;
        }
        setBusy(true); setError(null);
        try {
            await verifyRegistrationCode({ email: email.trim(), code: code.trim(), sessionId });
            setStep("profile");
        } catch (err) {
            setError(resolveErrorMessage(err, { t, lang, fallbackKey: "auth.verifyCodeInvalid" }));
        } finally {
            setBusy(false);
        }
    };

    const validateProfile = () => {
        const v = {};
        if (!profile.etunimi.trim()) v.etunimi = t("auth.shared.etunimiRequired");
        if (!profile.sukunimi.trim()) v.sukunimi = t("auth.shared.sukunimiRequired");
        if (!profile.puhelin.trim()) v.puhelin = t("auth.shared.puhelinRequired");
        if (profile.password.length < 8) v.password = t("auth.shared.min8Chars");
        if (profile.isYritys) {
            if (!profile.yritysNimi.trim()) v.yritysNimi = t("auth.shared.companyNameRequired");
            if (!profile.ytunnus.trim()) v.ytunnus = t("auth.register.ytunnusRequired");
        }
        if (!profile.legalAccepted) v.legalAccepted = t("auth.register.legalRequired");
        return v;
    };

    const submitProfile = async (e) => {
        e.preventDefault();
        if (!sessionId) { onBack(); return; }
        const v = validateProfile();
        setErrors(v);
        if (Object.keys(v).length) return;
        setBusy(true); setError(null);
        try {
            const authData = await completeRegistration({
                email: email.trim(),
                sessionId,
                password: profile.password,
                etunimi: profile.etunimi.trim(),
                sukunimi: profile.sukunimi.trim(),
                puhelin: profile.puhelin.trim(),
                isYritys: profile.isYritys,
                yritysNimi: profile.yritysNimi.trim(),
                ytunnus: profile.ytunnus.trim() || null,
            });
            // Record Käyttöehdot + Tietosuoja acceptance against the user row.
            // Fire-and-forget — `reaffirmAcceptances` swallows + reports to Sentry
            // on failure rather than blocking the user. The FE-side checkbox above
            // is the gate; this call is the audit trail.
            reaffirmAcceptances(authData);
            // Successful completion sets auth → parent re-renders to authenticated state.
        } catch (err) {
            setError(resolveErrorMessage(err, { t, lang, fallbackKey: "auth.registerFailed" }));
        } finally {
            setBusy(false);
        }
    };

    if (step === "code") {
        return (
            <Box component="form" onSubmit={submitCode} sx={{ maxWidth: 420 }}>
                <Stack spacing={3}>
                    <RegisterStepper step={step} />
                    <Typography variant="h4" component="h1">{t("auth.register.checkEmailTitle")}</Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {sendingCode
                            ? tRich("auth.register.sendingCodeIntro", { email: <strong>{email}</strong> })
                            : tRich("auth.register.codeSentIntro", { email: <strong>{email}</strong> })}
                    </Typography>
                    {error && <Alert severity="error">{error}</Alert>}
                    {sendError && <Alert severity="error">{sendError}</Alert>}
                    <TextField
                        label={t("auth.shared.verificationCodeLabel")} required fullWidth autoFocus
                        value={code} onChange={(e) => setCode(e.target.value)}
                        slotProps={{ htmlInput: { inputMode: "numeric", maxLength: 4 } }}
                        disabled={sendingCode || !sessionId}
                    />
                    <BusyButton
                        type="submit" size="large" variant="contained" color="warning"
                        loading={busy} disabled={sendingCode || !sessionId}
                    >
                        {busy ? t("auth.register.verifying") : t("auth.register.verifyCodeButton")}
                    </BusyButton>
                    <Button size="small" onClick={onBack}>
                        {t("auth.register.useDifferentEmail")}
                    </Button>
                </Stack>
            </Box>
        );
    }

    // step === "profile"
    return (
        <Box component="form" onSubmit={submitProfile} sx={{ maxWidth: 480 }}>
            <Stack spacing={3}>
                <RegisterStepper step={step} />
                <Typography variant="h4" component="h1">{t("auth.register.profileTitle")}</Typography>
                {error && <Alert severity="error">{error}</Alert>}
                <FormControlLabel
                    control={<Checkbox checked={profile.isYritys} onChange={updateProfile("isYritys")} />}
                    label={t("auth.register.isCompanyLabel")}
                />
                {profile.isYritys && (
                    <Stack spacing={2}>
                        <TextField
                            label={t("auth.shared.companyNameLabel")} required fullWidth
                            value={profile.yritysNimi} onChange={updateProfile("yritysNimi")}
                            error={!!errors.yritysNimi} helperText={errors.yritysNimi}
                        />
                        <TextField
                            label={t("auth.shared.ytunnusLabel")} required fullWidth
                            value={profile.ytunnus} onChange={updateProfile("ytunnus")}
                            error={!!errors.ytunnus} helperText={errors.ytunnus}
                        />
                    </Stack>
                )}
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                        label={t("auth.shared.etunimiLabel")} required fullWidth
                        value={profile.etunimi} onChange={updateProfile("etunimi")}
                        error={!!errors.etunimi} helperText={errors.etunimi}
                    />
                    <TextField
                        label={t("auth.shared.sukunimiLabel")} required fullWidth
                        value={profile.sukunimi} onChange={updateProfile("sukunimi")}
                        error={!!errors.sukunimi} helperText={errors.sukunimi}
                    />
                </Stack>
                <TextField
                    label={t("auth.shared.puhelinLabel")} type="tel" required fullWidth
                    value={profile.puhelin} onChange={updateProfile("puhelin")}
                    error={!!errors.puhelin} helperText={errors.puhelin}
                />
                <TextField
                    label={t("auth.shared.passwordLabel")} type="password" required fullWidth
                    value={profile.password} onChange={updateProfile("password")}
                    error={!!errors.password} helperText={errors.password || t("auth.shared.min8Chars")}
                />
                <Box>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={profile.legalAccepted}
                                onChange={updateProfile("legalAccepted")}
                                sx={{ alignSelf: "flex-start", mt: -0.5 }}
                            />
                        }
                        label={
                            <Typography variant="body2" sx={{ fontSize: 13, lineHeight: 1.55 }}>
                                {tRich("auth.register.legalConsent", {
                                    terms: (
                                        <Link component={RouterLink} to={KAYTTOEHDOT_ROUTE} target="_blank" rel="noopener" underline="hover">
                                            {t("auth.register.termsLinkText")}
                                        </Link>
                                    ),
                                    privacy: (
                                        <Link component={RouterLink} to={TIETOSUOJA_ROUTE} target="_blank" rel="noopener" underline="hover">
                                            {t("auth.register.privacyLinkText")}
                                        </Link>
                                    ),
                                })}
                            </Typography>
                        }
                        sx={{ alignItems: "flex-start", mr: 0 }}
                    />
                    {errors.legalAccepted && (
                        <Typography variant="caption" color="error" sx={{ display: "block", pl: 4, mt: 0.5 }}>
                            {errors.legalAccepted}
                        </Typography>
                    )}
                </Box>
                <BusyButton
                    type="submit" size="large" variant="contained" color="warning" loading={busy}
                >
                    {busy ? t("auth.register.creatingAccount") : t("auth.register.createAccountButton")}
                </BusyButton>
            </Stack>
        </Box>
    );
}
